import React from "react";
import { Users, BookOpen, MessagesSquare, BarChart3, Lightbulb, Target, Star, Award } from "lucide-react";

const GOLD = { backgroundImage: "linear-gradient(180deg,#fff3b0,#f5c542 45%,#c98a0b)", WebkitBackgroundClip: "text", backgroundClip: "text" };

// Header of "Program Pilihan Kelas Membaca PMM"
export function PilihanHeader({ ms, setLightbox }) {
  const L = (m, e) => (ms ? m : e);
  return (
    <div className="relative mx-auto max-w-5xl" data-testid="pilihan-header">
      <div className="text-center">
        <p className="font-script text-4xl text-navy-900 sm:text-5xl">Program</p>
        <h2 className="-mt-1 font-display text-6xl font-extrabold uppercase leading-none tracking-tight text-transparent drop-shadow-[0_3px_0_rgba(15,31,74,0.35)] sm:text-8xl" style={GOLD}>
          {L("Pilihan", "Choice")}
        </h2>
        <div className="relative mx-auto mt-3 inline-block">
          <div className="absolute -left-4 top-2 h-full w-6 skew-y-12 rounded-l bg-navy-900" />
          <div className="absolute -right-4 top-2 h-full w-6 -skew-y-12 rounded-r bg-navy-900" />
          <div className="relative rounded-lg border-2 border-yellow-400 bg-gradient-to-r from-navy-800 via-navy-600 to-navy-800 px-6 py-2 shadow-xl">
            <span className="font-display text-2xl font-extrabold uppercase tracking-wide text-white sm:text-3xl">{L("Kelas Membaca PMM", "PMM Reading Class")}</span>
          </div>
        </div>
        <p className="mt-4 font-display text-sm font-extrabold uppercase tracking-wide text-navy-900 sm:text-base">
          {L("Pilih cara belajar anda, ", "Pick how you learn, ")}<span className="text-orange-600">{L("capai kejayaan anda!", "achieve your success!")}</span>
        </p>
      </div>

      <div className="relative mx-auto mt-8 max-w-3xl">
        <button type="button" onClick={() => setLightbox("/landing/pmm/poster-pilihan.jpg")} className="pmm-cinematic block w-full overflow-hidden rounded-[2rem] border-4 border-white shadow-2xl ring-4 ring-yellow-400/70" data-testid="pilihan-kids-photo">
          <img src="/landing/pmm/pilihan-kids-3pelajar.jpg" alt={L("Murid PMM bersama modul Program Membaca PMM", "PMM students holding the PMM Reading Program module")} className="h-auto w-full transition-transform duration-700 hover:scale-105" loading="lazy" />
        </button>
        <div className="pmm-pop absolute -right-3 -top-6 grid h-28 w-28 rotate-6 place-items-center rounded-full border-4 border-yellow-400 bg-gradient-to-br from-orange-600 to-orange-800 p-2 text-center shadow-2xl sm:-right-10 sm:h-36 sm:w-36">
          <span className="font-display text-[11px] font-extrabold uppercase leading-tight text-white sm:text-sm">
            <span className="flex justify-center gap-0.5 pb-0.5">{[0, 1, 2].map((i) => <Star key={i} className="h-3 w-3 fill-yellow-300 text-yellow-300" />)}</span>
            {L("Anak Mesti", "Kids Must")}<br /><span className="text-yellow-300">{L("Lancar", "Read")}</span><br />{L("Membaca!", "Fluently!")}
          </span>
        </div>
        <div className="absolute -left-3 -top-5 hidden -rotate-6 rounded-2xl bg-yellow-300 px-4 py-2 shadow-xl sm:-left-12 sm:block">
          <p className="font-display text-sm font-extrabold uppercase leading-tight text-navy-900">{L("Anak", "Kids")}<br />{L("Lebih Fokus", "More Focus")}<br />{L("Lebih Yakin", "More Confident")}<br />{L("Lebih Cerdas", "Smarter")}</p>
        </div>
        <p className="absolute -bottom-6 left-4 -rotate-6 rounded-xl bg-white/95 px-3 py-1 font-script text-xl leading-tight text-navy-900 shadow-lg sm:-left-8 sm:text-2xl">{L("Pendidikan Berkualiti, Generasi Hebat!", "Quality Education, A Great Generation!")}</p>
      </div>
    </div>
  );
}

const STRIP = [
  { icon: Users, ms: "Bimbingan Guru Pakar", en: "Expert Teacher Guidance", c: "from-orange-500 to-orange-700" },
  { icon: BookOpen, ms: "Aktiviti Menarik", en: "Fun Activities", c: "from-navy-600 to-navy-800" },
  { icon: MessagesSquare, ms: "Interaksi Langsung", en: "Direct Interaction", c: "from-orange-500 to-orange-700" },
  { icon: BarChart3, ms: "Fokus & Disiplin", en: "Focus & Discipline", c: "from-navy-600 to-navy-800" },
  { icon: Lightbulb, ms: "Pemantauan Kemajuan", en: "Progress Monitoring", c: "from-orange-500 to-orange-700" },
  { icon: Target, ms: "Capai Kejayaan", en: "Achieve Success", c: "from-navy-600 to-navy-800" },
];

// Footer of "Program Pilihan" section: 6 icons + slogan banner
export function PilihanFooter({ ms }) {
  const L = (m, e) => (ms ? m : e);
  return (
    <div className="mt-14" data-testid="pilihan-footer">
      <div className="grid grid-cols-3 gap-4 sm:grid-cols-6">
        {STRIP.map((s, i) => (
          <div key={s.en} className="pmm-pop flex flex-col items-center gap-2 text-center" style={{ animationDelay: `${i * 0.07}s` }}>
            <span className={`grid h-16 w-16 place-items-center rounded-full border-4 border-white bg-gradient-to-br ${s.c} text-white shadow-xl ring-2 ring-yellow-400 transition-transform duration-300 hover:-translate-y-1 hover:scale-110 sm:h-20 sm:w-20`}>
              <s.icon className="h-7 w-7 sm:h-8 sm:w-8" />
            </span>
            <span className="font-display text-[10px] font-extrabold uppercase leading-tight text-navy-900 sm:text-xs">{ms ? s.ms : s.en}</span>
          </div>
        ))}
      </div>
      <div className="relative mt-10 flex flex-col items-center gap-6 overflow-hidden rounded-[2rem] bg-gradient-to-r from-navy-950 via-navy-900 to-navy-800 p-6 shadow-2xl sm:flex-row sm:p-8">
        <div className="pointer-events-none absolute -right-10 -top-10 h-40 w-40 rounded-full bg-orange-500/30 blur-3xl" />
        <div className="w-48 shrink-0 space-y-1">
          {[{ t: L("Membaca", "Read"), c: "bg-navy-600 w-[94%]" }, { t: L("Fikir", "Think"), c: "bg-orange-600 w-full" }, { t: L("Berjaya", "Succeed"), c: "bg-navy-500 w-[90%]" }].map((b, i) => (
            <div key={b.t} className={`flex items-center justify-center gap-1 rounded-r-md rounded-l-sm border-l-4 border-yellow-300 py-1.5 font-display text-sm font-extrabold uppercase text-white shadow-md ${b.c}`}>
              {b.t}{i === 2 && <Star className="h-3.5 w-3.5 fill-yellow-300 text-yellow-300" />}
            </div>
          ))}
        </div>
        <div className="relative flex-1 -rotate-1 rounded-2xl bg-gradient-to-r from-yellow-300 to-yellow-400 px-6 py-4 text-center shadow-xl">
          <p className="font-script text-3xl leading-tight text-navy-950 sm:text-4xl">{L("Membaca Hari Ini, Masa Depan Lebih Cerah!", "Read Today, A Brighter Tomorrow!")}</p>
        </div>
        <div className="flex shrink-0 flex-col items-center text-center">
          <Award className="h-8 w-8 text-yellow-300" />
          <span className="font-display text-3xl font-extrabold leading-none text-transparent" style={GOLD}>PMM</span>
          <span className="text-[10px] font-bold uppercase tracking-widest text-white/80">Setta Group Holdings</span>
        </div>
      </div>
    </div>
  );
}
